import { CacheDataVoyagerPayload } from '@/store/reducers/DataVoyagerWidgetStore';
import {
  DataVoyagerWidgetDataFromApi,
  getDataVoyagerWidgetDataFromApi,
} from '@/models/DataVoyager';
import { EventType } from '@/models/Interaction';
import { getChatAPI } from '@/api/ChatApi';
import { RootState } from '@/store/store';
import { ThreadId } from '@/models/Thread';

import { createAsyncThunk } from '@reduxjs/toolkit';

export type FetchDataVoyagerError = {
  errorMessage: string;
};

export const fetchLatestDataVoyagerState = createAsyncThunk<
  CacheDataVoyagerPayload,
  ThreadId,
  { state: RootState; rejectValue: FetchDataVoyagerError }
>('data/fetchLatestDataVoyagerState', async (threadId, { rejectWithValue }) => {
  try {
    const chatApi = getChatAPI();
    const { data } = await chatApi.getLatestEventOfType(
      threadId,
      EventType.WIDGET_DATA_VOYAGER,
    );
    if (!data) {
      return rejectWithValue({ errorMessage: 'No data found' });
    }
    const dvData: DataVoyagerWidgetDataFromApi | null = data.last_event
      ? (data.last_event?.data as DataVoyagerWidgetDataFromApi)
      : null;

    if (!dvData) {
      return rejectWithValue({ errorMessage: 'No Data Voyager logs found' });
    }

    return { threadId, dvData: getDataVoyagerWidgetDataFromApi(dvData) };
  } catch (dvError) {
    if (dvError instanceof Error) {
      return rejectWithValue({ errorMessage: dvError.message });
    }

    throw dvError;
  }
});

export const fetchDataVoyagerStateByWidgetId = createAsyncThunk<
  CacheDataVoyagerPayload,
  { threadId: ThreadId; widgetId: string },
  { state: RootState; rejectValue: FetchDataVoyagerError }
>(
  'data/fetchDataVoyagerStateByWidgetId',
  async ({ threadId, widgetId }, { rejectWithValue }) => {
    try {
      const chatApi = getChatAPI();
      const { data } = await chatApi.getEventByWidgetId({
        threadId,
        widgetEventType: EventType.WIDGET_DATA_VOYAGER,
        widgetId,
      });
      if (!data) {
        return rejectWithValue({ errorMessage: 'No data found' });
      }
      const widgetData = data?.widget?.widget_data ?? null;
      const dvData: DataVoyagerWidgetDataFromApi | null = widgetData
        ? (widgetData as DataVoyagerWidgetDataFromApi)
        : null;

      if (!dvData) {
        return rejectWithValue({ errorMessage: 'No Data Voyager logs found' });
      }

      return { threadId, dvData: getDataVoyagerWidgetDataFromApi(dvData) };
    } catch (dvError) {
      if (dvError instanceof Error) {
        return rejectWithValue({ errorMessage: dvError.message });
      }

      throw dvError;
    }
  },
);
